import React, { ReactElement, useState } from "react";
import { Button, Card, CardBody, Col, Row, Text } from "@jarvis-catalyst/ui-components";
import { useNavigate } from "react-router-dom";
import { FIFOptionList } from "../../config/constants";
import { ClientUnlockLocationState } from "../../types";
import { lockUnlockAccountOption } from "../../config/data/userOptions";
import { useUser } from "../../hooks/useUser";
import { validateIfHasPermissionToAccessView } from "../../utils/filterPermission";
import { useKeycloakServiceProviders } from "../../hooks/useKeycloakServiceProvider";
import { SubmitHandler, useForm } from "react-hook-form";

type BlockUnlockFormData = {
  serviceProvider: string;
  documentType: string;
  documentNumber: string;
};

type Props = {
  title: string;
};

const ClientUnlockForm = ({ title }: Props): ReactElement => {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm<BlockUnlockFormData>();

  const onSubmit: SubmitHandler<BlockUnlockFormData> = async ({
    documentNumber,
    documentType,
    serviceProvider
  }) => {
    navigate("/clientUnlockAction", {
      state: {
        form: {
          serviceProvider,
          tipoDocumento: documentType,
          email: documentNumber,
          nroDocumento: documentNumber
        },
        title: title
      } as ClientUnlockLocationState
    });
  };

  const { keycloakServiceProviderUseQuery } = useKeycloakServiceProviders();
  const [lockUnlockAccount] = useState(lockUnlockAccountOption);
  const { permission } = useUser();

  const hasValidPermission = lockUnlockAccount.some(({ validPermissions }) => {
    return validateIfHasPermissionToAccessView({
      validPermissionsView: validPermissions,
      userPermission: permission,
      action: "update"
    });
  });

  return (
    <>
      <Row className="d-flex justify-content-center align-items-center">
        <Col md={8} lg={6} xs={12}>
          <Card className="shadow">
            <CardBody>
              <div className="mb-3 mt-md-4">
                <h2 className="fw-bold mb-2 text-uppercase text-center">{title}</h2>
                <hr />
                <form className="mb-1" onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-1">
                    <div className="mb-1">
                      <Text>Pais/Negocio:</Text>
                      <select
                        className={`form-control ${errors.serviceProvider ? "is-invalid" : ""}`}
                        {...register(`serviceProvider`, {
                          required: { value: true, message: "El pais/negocio es obligatorio" },
                          value: ""
                        })}
                      >
                        <option disabled value="">
                          Seleccione una opción
                        </option>
                        {keycloakServiceProviderUseQuery.data?.map((item, index) => (
                          <option key={index} value={item.serviceProvider}>
                            {item.serviceProvider}
                          </option>
                        ))}
                      </select>
                      {errors.serviceProvider && (
                        <>
                          <div className="text-center mt-2">
                            <p className="fst-italic" style={{ color: "red", fontSize: "14px" }}>
                              {errors.serviceProvider.message}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="mb-3">
                      <Text>Tipo de documento:</Text>
                      <select
                        className={`form-control ${errors.documentType ? "is-invalid" : ""}`}
                        {...register(`documentType`, {
                          required: { value: true, message: "El tipo de documento es obligatorio" },
                          value: ""
                        })}
                      >
                        <option disabled value="">
                          Seleccione una opción
                        </option>
                        {FIFOptionList?.map((item, index) => (
                          <option value={item.label} key={index}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                      {errors.documentType && (
                        <>
                          <div className="text-center mt-2">
                            <p className="fst-italic" style={{ color: "red", fontSize: "14px" }}>
                              {errors.documentType.message}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="mb-3">
                      <Text>Número de documento:</Text>
                      {watch("documentType")?.includes("DNI") && watch("serviceProvider")?.includes("PE") ? (
                        <>
                          <input
                            type="text"
                            placeholder="Ingrese 8 dígitos"
                            className={`form-control ${errors.documentNumber ? "is-invalid" : ""}`}
                            {...register("documentNumber", {
                              required: { value: true, message: "El número de documento es obligatorio" },
                              pattern: {
                                value: /^\d{8}$/,
                                message:
                                  "El número de documento debe contener exactamente 8 dígitos y solo números."
                              },
                              minLength: {
                                value: 8,
                                message: "El número de documento debe contener exactamente 8 dígitos."
                              },
                              maxLength: {
                                value: 8,
                                message: "El número de documento debe contener exactamente 8 dígitos."
                              }
                            })}
                          />
                        </>
                      ) : (
                        <>
                          <input
                            type="text"
                            className={`form-control ${errors.documentNumber ? "is-invalid" : ""}`}
                            {...register("documentNumber", {
                              required: { value: true, message: "El número de documento es obligatorio" }
                            })}
                          />
                        </>
                      )}
                      {errors.documentNumber && (
                        <>
                          <div className="text-center mt-2">
                            <p className="fst-italic" style={{ color: "red", fontSize: "14px" }}>
                              {errors.documentNumber.message}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="d-grid">
                    <Button
                      color="primary"
                      className="mt-2 text-center"
                      disabled={!hasValidPermission}
                      type="submit"
                    >
                      Check User
                    </Button>
                    <Button
                      color="bordered"
                      type="button"
                      className="mt-2 text-center"
                      onClick={() => navigate("/")}
                    >
                      Back
                    </Button>
                  </div>
                </form>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default ClientUnlockForm;
